import React, { useRef, useState } from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';

// Replaces the old one-click "Cancel your subscription" form + native confirm() with a
// short exit survey. Roughly 90% of churn is voluntary, yet we used to capture no reason
// at all. The visible button now opens a modal asking why the user is leaving (a required
// reason + an always-available optional free-text box); confirming submits the hidden form
// to /charges/cancel exactly as before, with the reason fields attached. "Keep my
// subscription" (or the × / backdrop) simply closes the modal — no dark patterns.
const CancelSubscriptionSurvey = ({
  action,
  stripeId,
  authenticityToken,
  cancelButtonLabel,
  title,
  subtitle,
  textPlaceholder,
  confirmLabel,
  keepLabel,
  reasons
}) => {
  const popupRef = useRef();
  const formRef = useRef();
  // The partial renders once per active subscription, so a user with more than one
  // subscription can have several of these surveys on the page at once. Give each
  // radio group an instance-unique name so the native "one radio per name" behaviour
  // never lets one survey's selection clear another's (matches UpgradeToAnnualModal).
  const radioGroupName = useRef(`cancel-survey-reason-${Math.random().toString(36).slice(2)}`);
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');

  const open = () => {
    if (popupRef.current) {
      popupRef.current.style.display = 'block';
    }
  };

  const close = () => {
    if (popupRef.current) {
      popupRef.current.style.display = 'none';
    }
  };

  const confirmCancel = () => {
    if (!reason) {
      return;
    }
    formRef.current.submit();
  };

  const content = (
    <div className="cancel-survey">
      <h3 className="cancel-survey__title">{title}</h3>
      <p className="cancel-survey__subtitle">{subtitle}</p>

      <ul className="cancel-survey__reasons" style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
        {reasons.map((option) => (
          <li key={option.value} style={{ margin: '8px 0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' }}>
              <input
                type="radio"
                name={radioGroupName.current}
                value={option.value}
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
              />
              {option.label}
            </label>
          </li>
        ))}
      </ul>

      <textarea
        className="cancel-survey__text"
        placeholder={textPlaceholder}
        aria-label={textPlaceholder}
        value={reasonText}
        maxLength={2000}
        onChange={(event) => setReasonText(event.target.value)}
        style={{ width: '100%', minHeight: '80px', marginBottom: '16px' }}
      />

      <div className="cancel-survey__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button
          type="button"
          className="btn subscription-card-action-primary"
          disabled={!reason}
          onClick={confirmCancel}
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          className="btn subscription-card-action-secondary"
          onClick={close}
        >
          {keepLabel}
        </button>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <input
        type="button"
        className="btn btn-default"
        value={cancelButtonLabel}
        onClick={open}
      />

      <form action={action || '/charges/cancel'} method="POST" ref={formRef} style={{ display: 'none' }}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="stripe_id" value={stripeId} />
        <input type="hidden" name="cancellation_reason" value={reason} />
        <input type="hidden" name="cancellation_reason_text" value={reasonText} />
      </form>

      <ContentPopup
        myRef={popupRef}
        autoOpen={false}
        content={content}
        cancelButton={false}
        notWide={true}
      />
    </React.Fragment>
  );
};

export default CancelSubscriptionSurvey;
